import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getApiUrl } from "@/lib/config";
import { queryClient } from "@/lib/queryClient";
import type { CrmEntity } from "./fields";

// ── Contracts (FillSign.Infrastructure/Crm/CrmService.cs) ─────────────────────────────────────

export interface CrmMember { userId: string; name: string }

export interface CrmContext {
  enabled: boolean;
  hasCompany: boolean;
  isAdmin: boolean;
  userId: string;
  userName?: string;
  companyId?: string;
  members: CrmMember[];
}

export interface CrmRecord {
  id: string;
  entity: CrmEntity;
  values: Record<string, any>;
  /** Display names of user fields (ownerId, createdBy, modifiedBy, assignedToUserId). */
  names: Record<string, string | null>;
}

export interface CrmViewColumn { key: string; width?: number | null }
export type CrmConditionOp = "eq" | "neq" | "contains" | "notcontains" | "empty" | "notempty" | "gt" | "lt";
export interface CrmViewCondition { field: string; op: CrmConditionOp; value?: string | null }
export interface CrmViewFilter { state: "active" | "inactive" | "all"; owner: "me" | "any"; conditions: CrmViewCondition[] }
export interface CrmViewSort { key: string; dir: "asc" | "desc" }

export interface CrmView {
  id: string;
  entity: CrmEntity;
  systemKey?: string | null;
  name: string;
  namePt?: string | null;
  nameEn?: string | null;
  isSystem: boolean;
  isShared: boolean;
  isMine: boolean;
  canEdit: boolean;
  overridden: boolean;
  ownerName?: string | null;
  columns: CrmViewColumn[];
  filter: CrmViewFilter;
  sort: CrmViewSort;
}

export interface CrmViewInput {
  entity: CrmEntity;
  name: string;
  isShared: boolean;
  columns: CrmViewColumn[];
  filter: CrmViewFilter;
  sort: CrmViewSort;
}

export interface CrmLayoutField { key: string; required: boolean }
export interface CrmLayoutSection { id: string; label?: string | null; fields: CrmLayoutField[] }
export interface CrmLayout { sections: CrmLayoutSection[] }
export interface CrmFormResponse { layout: CrmLayout; isCustom: boolean; canCustomize: boolean }

export interface CrmAuditChange { field: string; old?: string | null; new?: string | null }

export interface CrmTimelineItem {
  id: string;
  kind: "note" | "task" | "appointment" | "call" | "email" | "signature" | "signed" | "system" | string;
  subject?: string | null;
  description?: string | null;
  at: string;
  byName?: string | null;
  status?: string | null;
  dueAt?: string | null;
  startAt?: string | null;
  endAt?: string | null;
  location?: string | null;
  isCompleted: boolean;
  documentId?: string | null;
  editable: boolean;
  operation?: string | null;
  changes?: CrmAuditChange[] | null;
  /** Record the item belongs to: a client and its leads share one timeline. */
  regardingEntity?: CrmEntity | null;
  regardingId?: string | null;
  /** Name of that record when it is not the one open (client name or lead topic). */
  regardingLabel?: string | null;
}

export interface CrmActivityInput {
  type: "note" | "task" | "appointment" | "call" | "email";
  subject?: string | null;
  description?: string | null;
  dueAt?: string | null;
  startAt?: string | null;
  endAt?: string | null;
  location?: string | null;
  isCompleted?: boolean | null;
}

export interface CrmAuditEntry { id: string; operation: string; changedAt: string; userName?: string | null; changes: CrmAuditChange[] }

export interface CrmFile {
  documentId: string;
  name: string;
  status: string;
  createdAt: string;
  sentByName?: string | null;
  signers: string[];
  linked: boolean;
  /** Sent from a related record (the lead's client or one of the client's leads). */
  viaEntity?: CrmEntity | null;
  viaId?: string | null;
  viaLabel?: string | null;
}

export interface CrmAcceptResult { lead: CrmRecord; contactId: string; contactCreated: boolean }

// ── Transport ─────────────────────────────────────────────────────────────────────────────────

/** API failure with the CRM error contract: { code, message, details }. */
export class CrmError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: any) {
    super(message);
  }
  /** VALIDATION details as { field: code }. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    const list = this.details?.errors;
    if (Array.isArray(list)) for (const e of list) if (e?.field) out[e.field] = e.code || "invalid";
    return out;
  }
}

export const plural = (entity: CrmEntity) => (entity === "lead" ? "leads" : "contacts");

export async function crmFetch<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = localStorage.getItem("token");
  const res = await fetch(getApiUrl(path), {
    method,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    // Same behaviour as the rest of the app: an expired session goes back to login.
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    if (window.location.pathname !== "/login") window.location.href = "/login";
    throw new CrmError(401, "UNAUTHORIZED", "Session expired");
  }
  const text = await res.text();
  let json: any = null;
  try { json = text ? JSON.parse(text) : null; } catch { json = null; }
  if (!res.ok) {
    throw new CrmError(res.status, json?.code || `HTTP_${res.status}`, json?.message || text || res.statusText, json?.details);
  }
  return json as T;
}

// ── Query keys and hooks ─────────────────────────────────────────────────────────────────────

export const crmKeys = {
  all: ["crm"] as const,
  context: ["crm", "context"] as const,
  list: (e: CrmEntity) => ["crm", e, "list"] as const,
  record: (e: CrmEntity, id: string) => ["crm", e, "record", id] as const,
  views: (e: CrmEntity) => ["crm", "views", e] as const,
  form: (e: CrmEntity) => ["crm", "form", e] as const,
  timeline: (e: CrmEntity, id: string) => ["crm", e, "timeline", id] as const,
  audit: (e: CrmEntity, id: string) => ["crm", e, "audit", id] as const,
  files: (e: CrmEntity, id: string) => ["crm", e, "files", id] as const,
};

// Record data refreshes when a screen mounts and it is older than 15 s (mutations invalidate it right away);
// keeps navigation snappy without hammering the API (120 requests/min per user).
const FRESH = { staleTime: 15_000 };

export const useCrmContext = (enabled = true) =>
  useQuery<CrmContext, CrmError>({ queryKey: crmKeys.context, queryFn: () => crmFetch("GET", "/api/crm/context"), staleTime: 300_000, enabled });

/**
 * Whether the company has the CRM module on (menu items Clientes/Leads). Set by company admins in
 * Settings; the last answer is remembered per user so the items do not pop in on every load.
 */
export function useCrmMenuFlag(userId?: string | null): boolean {
  const key = `fs:crm:enabled:${userId || ""}`;
  const { data } = useCrmContext(!!userId);
  const cached = useMemo(() => {
    try { return localStorage.getItem(key) === "1"; } catch { return false; }
  }, [key]);
  const enabled = data ? data.enabled && data.hasCompany : cached;
  useEffect(() => {
    if (!data) return;
    try { localStorage.setItem(key, data.enabled && data.hasCompany ? "1" : "0"); } catch { /* storage unavailable */ }
  }, [data, key]);
  return !!userId && enabled;
}

export const useCrmList = (entity: CrmEntity, enabled = true) =>
  useQuery<CrmRecord[], CrmError>({ queryKey: crmKeys.list(entity), queryFn: () => crmFetch("GET", `/api/crm/${plural(entity)}`), enabled, ...FRESH });

export const useCrmRecord = (entity: CrmEntity, id: string | null, enabled = true) =>
  useQuery<CrmRecord, CrmError>({
    queryKey: crmKeys.record(entity, id || "new"),
    queryFn: () => crmFetch("GET", `/api/crm/${plural(entity)}/${id}`),
    enabled: enabled && !!id,
    // Opening a row from the grid shows the list's copy at once; it refreshes if older than 15 s.
    initialData: () => (id ? queryClient.getQueryData<CrmRecord[]>(crmKeys.list(entity))?.find((r) => r.id === id) : undefined),
    initialDataUpdatedAt: () => queryClient.getQueryState(crmKeys.list(entity))?.dataUpdatedAt,
    ...FRESH,
  });

export const useCrmViews = (entity: CrmEntity, enabled = true) =>
  useQuery<CrmView[], CrmError>({ queryKey: crmKeys.views(entity), queryFn: () => crmFetch("GET", `/api/crm/views?entity=${entity}`), enabled });

export const useCrmForm = (entity: CrmEntity, enabled = true) =>
  useQuery<CrmFormResponse, CrmError>({ queryKey: crmKeys.form(entity), queryFn: () => crmFetch("GET", `/api/crm/forms/${entity}`), enabled });

export const useCrmTimeline = (entity: CrmEntity, id: string | null) =>
  useQuery<CrmTimelineItem[], CrmError>({
    queryKey: crmKeys.timeline(entity, id || "new"),
    queryFn: () => crmFetch("GET", `/api/crm/${plural(entity)}/${id}/timeline`),
    enabled: !!id,
    ...FRESH,
  });

export const useCrmAudit = (entity: CrmEntity, id: string | null, enabled = true) =>
  useQuery<CrmAuditEntry[], CrmError>({
    queryKey: crmKeys.audit(entity, id || "new"),
    queryFn: () => crmFetch("GET", `/api/crm/${plural(entity)}/${id}/audit`),
    enabled: enabled && !!id,
    ...FRESH,
  });

export const useCrmFiles = (entity: CrmEntity, id: string | null, enabled = true) =>
  useQuery<CrmFile[], CrmError>({
    queryKey: crmKeys.files(entity, id || "new"),
    queryFn: () => crmFetch("GET", `/api/crm/${plural(entity)}/${id}/files`),
    enabled: enabled && !!id,
    ...FRESH,
  });

/** After any change to a record: refresh its list, timeline and audit (record data is set directly). */
export function invalidateRecord(entity: CrmEntity, id?: string) {
  queryClient.invalidateQueries({ queryKey: crmKeys.list(entity) });
  if (id) {
    queryClient.invalidateQueries({ queryKey: crmKeys.timeline(entity, id) });
    queryClient.invalidateQueries({ queryKey: crmKeys.audit(entity, id) });
  }
}

export function setRecordCache(record: CrmRecord) {
  queryClient.setQueryData(crmKeys.record(record.entity, record.id), record);
}
